import classNames from 'classnames';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import type { TelegramChat, TelegramChatDetail } from '@/types/api';
import { formatDate, formatDateTime, leadTitle, shownEmail } from '@/utils/format';
import { chatKindLabel } from '@/utils/labels';
import http from '@/utils/http';
import { useApi } from '@/utils/use-api';

import { BotIcon } from '@/components/ui/bot-icon';
import { btn, field } from '@/components/ui/styles';

import { ChatState } from './chat-state';
import { useClosing } from '@/utils/use-closing';

/** Confirms who the client is: links the chat to the lead with this email, creating it if needed (TG-2, LEAD-6). */
const LinkForm = ({ chat, onLinked }: { chat: TelegramChat; onLinked: () => void }) => {
  // A lead without email is proposed by its handle: link to it without showing its placeholder.
  const byHandle = chat.claimed_via === 'handle' && Boolean(chat.matching_lead_id) && !shownEmail(chat.claimed_email);
  const claimed = shownEmail(chat.claimed_email);
  const [email, setEmail] = useState(claimed);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setEmail(claimed);
  }, [chat.chat_id, claimed]);

  const existing = Boolean(chat.matching_lead_id) && email.trim().toLowerCase() === chat.claimed_email;
  const link = async (to: string) => {
    setBusy(true);
    setError(null);
    try {
      await http.post(`/telegram/chats/${chat.chat_id}/link`, { email: to });
      onLinked();
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      setError(status === 422 ? 'That does not look like an email address.' : 'Could not link the chat. Try again.');
    } finally {
      setBusy(false);
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void link(email);
  };

  return (
    <form onSubmit={submit} className="space-y-2">
      {byHandle && (
        <button type="button" disabled={busy} onClick={() => link(chat.claimed_email)} className={`${btn.primary} w-full md:w-auto`}>
          Link to the lead with this handle
        </button>
      )}
      <label className="block text-sm">
        <span className="text-fg-muted">Client’s email</span>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@company.com"
          className={`mt-1 ${field.input}`}
        />
      </label>
      {error && <p className="text-sm text-danger">{error}</p>}
      <button type="submit" disabled={busy || !email.trim()} className={`${btn.primary} w-full md:w-auto`}>
        {existing ? 'Link to the existing lead' : 'Link, creating the lead if needed'}
      </button>
      <p className="text-xs text-fg-faint">Check the email with the client first: linking puts this conversation on the lead’s record.</p>
    </form>
  );
};

/** One chat: who the client is, and the whole conversation. Full screen on a phone (UI-1, UI-2). */
const ChatPanel = ({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) => {
  const [closing, close] = useClosing(onClose);
  const { data, error, loading, reload } = useApi<TelegramChatDetail>(`/telegram/chats/${encodeURIComponent(id)}`);
  // useApi keeps the last chat while the next one loads: never show it, or unlink it, as this one.
  const chat = data && String(data.chat_id) === id ? data : null;
  const [unlinking, setUnlinking] = useState(false);
  const [unlinkFailed, setUnlinkFailed] = useState(false);
  useEffect(() => setUnlinkFailed(false), [id]);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);
  // Braces matter: scrollIntoView returns a Promise in current browsers, and an effect must not return one.
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [chat?.chat_id, chat?.message_history.length]);

  const changed = () => {
    reload();
    onChanged();
  };
  const unlink = async () => {
    setUnlinking(true);
    setUnlinkFailed(false);
    try {
      await http.post(`/telegram/chats/${encodeURIComponent(id)}/unlink`);
      changed();
    } catch {
      setUnlinkFailed(true);
    } finally {
      setUnlinking(false);
    }
  };

  return (
    <aside
      aria-label="Conversation"
      className={`fixed inset-y-0 right-0 z-40 flex w-full max-w-[440px] flex-col bg-surface-raised pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] shadow-e3 ${closing ? 'animate-drawer-out-right' : 'animate-drawer-in-right'}`}
    >
      <div className="flex items-start gap-3 px-4 py-3 md:px-5 md:py-4">
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-fg-strong">{chat?.title || (loading ? 'Loading…' : error ?? '')}</p>
          {chat && (
            <p className="text-xs text-fg-muted">
              {chatKindLabel(chat.kind)}
              {chat.member_count !== null && ` · ${chat.member_count === 1 ? '1 member' : `${chat.member_count} members`}`} · bot joined {formatDate(chat.joined_at)}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={close}
          aria-label="Close conversation"
          className="-mr-2 -mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-fg-muted hover:bg-surface-hover hover:text-fg md:mr-0 md:mt-0 md:h-8 md:w-8"
        >
          <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      </div>

      {chat && (
        <>
          <section className="border-t border-line px-4 py-4 md:px-5">
            <div className="mb-2 flex items-center justify-between gap-3">
              <h3 className="text-xs font-medium uppercase tracking-wide text-fg-muted">Client</h3>
              <ChatState chat={chat} />
            </div>
            {chat.lead_id ? (
              <div className="space-y-2 text-sm">
                <Link to={`/leads?lead=${chat.lead_id}`} className="inline-flex min-h-11 items-center text-brand [overflow-wrap:anywhere] hover:underline md:min-h-0">
                  {chat.lead_name && shownEmail(chat.lead_email) ? `${chat.lead_name} · ` : ''}
                  {shownEmail(chat.lead_email) || leadTitle(chat.lead_name, chat.lead_email)}
                </Link>
                <p className="text-xs text-fg-faint">
                  Confirmed {chat.confirmed_at ? formatDateTime(chat.confirmed_at) : ''}
                  {chat.confirmed_by ? ` by ${chat.confirmed_by}` : ''}
                </p>
                <button
                  type="button"
                  onClick={unlink}
                  disabled={unlinking}
                  className="inline-flex min-h-11 items-center text-xs text-fg-muted underline-offset-2 hover:text-fg hover:underline disabled:opacity-50 md:min-h-0"
                >
                  Unlink (wrong person)
                </button>
                {unlinkFailed && <p className="text-xs text-danger">Could not unlink the chat. Try again.</p>}
              </div>
            ) : (
              <>
                {chat.claimed_email ? (
                  <p className="mb-3 text-sm text-fg [overflow-wrap:anywhere]">
                    {chat.claimed_via === 'handle' ? (
                      <>
                        @{chat.claimed_by_username} has the Telegram handle of{' '}
                        {shownEmail(chat.claimed_email) ? (
                          <>
                            the lead <span className="text-fg-strong">{chat.claimed_email}</span>
                          </>
                        ) : (
                          'a lead without email'
                        )}
                        . Confirm it is them.
                      </>
                    ) : (
                      <>
                        {chat.claimed_by_username ? `@${chat.claimed_by_username}` : 'The client'} said their email is <span className="text-fg-strong">{chat.claimed_email}</span>.
                      </>
                    )}
                  </p>
                ) : (
                  <p className="mb-3 text-sm text-fg-muted">The client has not written an email yet. The bot asked for it; you can also type it here.</p>
                )}
                <LinkForm chat={chat} onLinked={changed} />
              </>
            )}
          </section>

          {chat.kind === 'group' && (
            <section className="border-t border-line px-4 py-4 md:px-5">
              <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-fg-muted">
                People{chat.member_count !== null && ` · ${chat.member_count} in the group`}
              </h3>
              {chat.people?.length ? (
                <ul className="flex flex-wrap gap-1.5">
                  {chat.people.map((p) => (
                    <li key={p.user_id} className={classNames('pill rounded-pill px-2.5 py-0.5 text-xs', p.left_at ? 'text-fg-faint' : 'text-fg')}>
                      {p.name || `@${p.username}`}
                      {p.name && p.username && <span className="text-fg-muted"> @{p.username}</span>}
                      {[p.admin && 'admin', p.team && 'team', p.left_at && 'left'].filter(Boolean).map((tag) => (
                        <span key={String(tag)} className="text-fg-faint"> · {tag}</span>
                      ))}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-fg-muted">Nobody seen yet.</p>
              )}
              <p className="mt-2 text-xs text-fg-faint">
                Telegram tells the bot how many members a group has and who its admins are, but not who everyone is: these are the admins, and whoever wrote or was added since the bot joined.
              </p>
            </section>
          )}

          <section className="flex min-h-0 flex-1 flex-col border-t border-line">
            <h3 className="px-4 pt-4 text-xs font-medium uppercase tracking-wide text-fg-muted md:px-5">Conversation ({chat.messages})</h3>
            <ol className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-3 md:px-5">
              {chat.message_history.map((m) => (
                <li key={m.message_id} className={classNames('flex flex-col', m.from_team ? 'items-end' : 'items-start')}>
                  <span className="mb-0.5 inline-flex items-center gap-1 text-xs text-fg-faint">
                    {m.from_bot && <BotIcon />}
                    {m.from_bot ? 'Prisma bot' : m.from_username || 'unknown'}
                    {m.from_team && !m.from_bot ? ' (team)' : ''} · {formatDateTime(m.sent_at)}
                    {m.edited_at ? ' · edited' : ''}
                  </span>
                  <p
                    className={classNames(
                      'max-w-[85%] whitespace-pre-wrap break-words rounded-xl px-3 py-2 text-sm text-fg',
                      m.from_bot ? 'bg-info/15' : m.from_team ? 'bg-brand/10' : 'bg-surface-inset',
                    )}
                  >
                    {m.text}
                  </p>
                </li>
              ))}
              <div ref={endRef} />
            </ol>
          </section>
        </>
      )}
    </aside>
  );
};

export default ChatPanel;
